import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Spinner,
  Text,
  Box,
  Badge,
  Divider,
  HStack,
  VStack,
  IconButton,
  Checkbox,
  Tooltip,
  Th,
  Thead,
  Table,
  Td,
  Tbody,
  Tr,
  Textarea,
} from "@chakra-ui/react";
import { showToast } from "components/toast/Toast";
import { USER_REQUIREMENTS_API } from "constants/endpoints";
import useApi from "hooks/useApi";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { PROPERTY_COLLECTIONS } from "constants/dbEnums";
import { SEND_LEAD_API } from "constants/endpoints";
import { GET_DIRECT_LEADS } from "constants/endpoints";
import { metersToKm } from "constants/commonFunctions";
import { formatDate } from "constants/commonFunctions";
import { whatsappMessageFormat } from "utils/shareToWhatsApp";
import { shareToWhatsApp } from "utils/shareToWhatsApp";

const NearbyLeadsModal = ({ isOpen, onClose, data }) => {
  const { apiCall, loading } = useApi();
  const [requirementData, setRequirementData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(50);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [confirmType, setConfirmType] = useState("");
  const [directLeads, setDirectLeads] = useState({});
  const [sendWhatsapp, setSendWhatsapp] = useState(false);
  const [whatsappMessage, setWhatsappMessage] =useState('');
  const [sendSMS, setSendSMS] =useState(false);

  const getFilterPayload = useCallback(() => {
    let { location, collection_type, ad_type, sharing_type } = data || {};
    const { city, locality } = location || {};
    let property_type;
    if (
      collection_type === PROPERTY_COLLECTIONS.PLOT ||
      collection_type === PROPERTY_COLLECTIONS.PG
    ) {
      property_type = collection_type;
    }
    if (sharing_type === "need_flat") ad_type = "rent";
    return {
      city,
      locality,
      ad_type,
      property_type,
    };
  }, [data]);

  const getUserRequirements = useCallback(
    async (page = 1) => {
      try {
        const {
          city = "",
          locality = "",
          ad_type = "",
          property_type = "",
        } = getFilterPayload();
        const res = await apiCall.get(
          `${USER_REQUIREMENTS_API}?page=${page}&limit=${limit}&city=${city}&locality=${locality}&ad_type=${ad_type}&property_type=${property_type}`
        );
        if (res?.data?.requirements) {
          setRequirementData(res?.data?.requirements || []);
          setTotalCount(res?.data?.count || 0);
        }
      } catch (error) {
        showToast({
          message: error?.message ?? "Failed to load Requirements",
          success: false,
          customStyle: {
            background: "#ff4d4f",
            color: "#fff",
            padding: "10px",
          },
        });
      }
    },
    [apiCall, limit]
  );

  const getDirectLeadsOfOwner = useCallback(async () => {
    try {
      const res = await apiCall.post(GET_DIRECT_LEADS, {
        broker_id: data?.owner?._id,
      });
      if (res?.data?.length) {
        const result = res.data.reduce((acc, item) => {
          acc[item.requirement_id] = { viewed: item.viewed };
          return acc;
        }, {});
        setDirectLeads(result);
        setSelectedLeads([]);
      }
    } catch (error) {
      showToast({
        message: error?.message ?? "Failed to load owner leads",
        success: false,
        customStyle: {
          background: "#ff4d4f",
          color: "#fff",
          padding: "10px",
        },
      });
    }
  }, [data]);

  useEffect(() => {
    getUserRequirements(currentPage);
    setSelectedLeads([]);
  }, [currentPage]);

  useEffect(() => {
    if (data?.owner) getDirectLeadsOfOwner();
  }, []);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    setSelectedLeads([]);
  };

  const handleCheckboxChange = (leadId) => {
    setSelectedLeads((prev) =>
      prev.includes(leadId)
        ? prev.filter((id) => id !== leadId)
        : [...prev, leadId]
    );
  };

  const handleShareLead = useCallback(
    async (requirements) => {
      try {
        const broker_id = data?.owner?._id;
        const payload = {
          broker_id,
          requirments: requirements,
          send_sms: sendSMS
        };
        setIsConfirmOpen(false);
        await apiCall.post(SEND_LEAD_API, payload);
        const existingLeads = { ...directLeads };
        requirements?.forEach((id) => (existingLeads[id] = {}));
        setDirectLeads(existingLeads);
      } catch (error) {
        showToast({
          message:
            error?.response?.data?.error?.message ||
            error?.message ||
            "Failed to Send Lead",
          success: false,
          customStyle: {
            background: "#ff4d4f",
            color: "#fff",
            padding: "10px",
          },
        });
      }
    },
    [data, sendSMS]
  );

  const handleSendSelectedLeads = async () => {
    // Add your logic to send selected leads
    await handleShareLead(selectedLeads);
    if(sendWhatsapp){
      shareToWhatsApp(whatsappMessage, data?.owner?.phone)
    }
    showToast({
      message: `${selectedLeads.length} Leads Sent Successfully!`,
      success: true,
    });
  };

  const handleSendAllLeads = async () => {
    // Add your logic to send all leads
    const allRequirements = requirementData.map((data) => data._id);
    await handleShareLead(allRequirements);
    if(sendWhatsapp){
      shareToWhatsApp(whatsappMessage, data?.owner?.phone)
    }
    showToast({
      message: `${allRequirements.length} Leads Sent Successfully!`,
      success: true,
    });
  };

  const totalPages = Math.ceil(totalCount / limit);

  const getPropertyTypeBadgeColor = (type) => {
    const colors = {
      PG: "purple",
      Apartment: "blue",
      House: "green",
      Villa: "orange",
    };
    return colors[type] || "gray";
  };

  const getAdTypeBadgeColor = (type) => {
    return type === "sell" ? "green" : "blue";
  };

  const openConfirmation = (type) => {
    setConfirmType(type);
    setIsConfirmOpen(true);
  };

  const formatLocation = useCallback((location) => {
    const { locality, city, sub_locality } = location || {};
    const parts = [];
    if (sub_locality) parts.push(sub_locality);
    if (locality) parts.push(locality);
    if (city) parts.push(city);
    return parts.join(", ");
  }, []);

  const getLeadsNames = () => {
    const hasSelected = selectedLeads.length;
    const firstItem = hasSelected
      ? requirementData.find((item) => item._id === selectedLeads[0])
      : requirementData?.[0];

    const firstName = firstItem?.user_data?.first_name || "";

    const remaining = hasSelected ? selectedLeads.length - 1 : totalCount - 1;

    return remaining > 0
      ? `${firstName} and ${remaining} others are`
      : `${firstName} is`;
  };

  const getCurrentMessage = useCallback(() => {
    const {collection_type, ad_type, created_by: user, location } =data || {}
    const map = {
      PG: "PG_direct_lead",
      share_flat: "share_flat_direct_lead",
      rent: "rent_direct_lead",
      sell: "sell_direct_lead",
    };
    const messageType =
      map[ad_type] || map[collection_type] || null;

    const template = whatsappMessageFormat[messageType];
    if (!template || !user) return;

    const leadUsers = getLeadsNames();
    const replacements = {
      "{{user}}": leadUsers || "",
      "{{location}}": `${location?.locality ? `${location?.locality},` : ''} ${location.city}`
    };

    const messageFormatted = Object.entries(replacements).reduce(
      (msg, [placeholder, value]) => msg.replace(placeholder, value),
      template
    );
    setWhatsappMessage(messageFormatted);
  }, [data, selectedLeads, requirementData, totalCount]);

  if (loading) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="full">
        <ModalOverlay />
        <ModalContent>
          <ModalBody>
            <Flex
              width={"100%"}
              height={"400px"}
              align={"center"}
              justify={"center"}
            >
              <Spinner size="lg" color="#22acee" thickness="6px" />
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="full">
        <ModalOverlay />
        <ModalContent>
          <ModalBody p={6}>
            <Flex direction={"column"} gap={4} width={"full"}>
              {/* Header with Close Button and Actions */}
              <Box
                position="sticky"
                top="0"
                zIndex="10"
                bg="white"
                pb={3}
                pt={2}
                borderBottom="1px solid"
                borderColor="gray.200"
              >
                <Flex
                  justify="space-between"
                  align="center"
                  flexWrap="wrap"
                  gap={3}
                >
                  {/* LEFT SIDE - Title and Count */}
                  <Box>
                    <Text fontSize="2xl" fontWeight="bold" mb={1}>
                      Nearby Leads
                    </Text>
                    <Text fontSize="md" color="gray.600">
                      Total: {totalCount} {totalCount === 1 ? "lead" : "leads"}
                    </Text>
                  </Box>

                  {/* RIGHT SIDE - Owner Info + Property Link */}
                  <Flex
                    direction="column"
                    align={{ base: "flex-start", md: "flex-end" }}
                    flex="1"
                  >
                    {/* Owner Name and Phone */}
                    <Text fontSize="md" color="gray.700" fontWeight="semibold">
                      Owner:{" "}
                      {data?.owner?.first_name
                        ? `${data?.owner?.first_name} ${data?.owner?.last_name}`
                        : "N/A"}
                    </Text>
                    <Text fontSize="md" color="gray.600">
                      📞 {data?.owner?.phone || "N/A"}
                    </Text>

                    {/* Property Slug as Link */}
                    {data?.slug_url && (
                      <Text
                        fontSize="sm"
                        color="blue.500"
                        fontWeight="medium"
                        _hover={{
                          textDecoration: "underline",
                          textDecorationColor: "blue.500",
                        }}
                      >
                        <a
                          href={`${process.env.REACT_APP_HEXAHOME_BASE_URL}/property-details/${data?.slug_url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {data?.slug_url}
                        </a>
                      </Text>
                    )}
                  </Flex>

                  {/* ACTION BUTTONS */}
                  <HStack spacing={3}>
                    {requirementData?.length ? (
                      <Button
                        colorScheme="blue"
                        size="md"
                        onClick={() => {
                          openConfirmation("all");
                          getCurrentMessage();
                        }}
                      >
                        Send All Leads
                      </Button>
                    ) : null}

                    {selectedLeads.length > 0 && (
                      <Button
                        colorScheme="green"
                        size="md"
                        onClick={() => {
                          openConfirmation("selected")
                          getCurrentMessage();
                        }}
                      >
                        Send Selected ({selectedLeads.length})
                      </Button>
                    )}

                    <Button
                      variant="outline"
                      borderRadius="full"
                      onClick={onClose}
                      size="sm"
                    >
                      ✕
                    </Button>
                  </HStack>
                </Flex>
              </Box>

              {/* Requirements List */}
              <VStack spacing={3} align="stretch" minH="500px">
                {requirementData.length === 0 ? (
                  <Flex
                    justify="center"
                    align="center"
                    minH="400px"
                    direction="column"
                    gap={2}
                  >
                    <Text fontSize="lg" color="gray.500">
                      No leads found
                    </Text>
                  </Flex>
                ) : (
                  <Box overflowX="auto">
                    <Table variant="simple" size="sm">
                      <Thead bg="gray.50" position="sticky" top={0} zIndex={1}>
                        <Tr>
                          <Th width="50px" textAlign="center">
                            <Checkbox
                              isChecked={
                                selectedLeads.length ===
                                  requirementData.length &&
                                requirementData.length > 0
                              }
                              isIndeterminate={
                                selectedLeads.length > 0 &&
                                selectedLeads.length < requirementData.length
                              }
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedLeads(
                                    requirementData.map((req) => req._id)
                                  );
                                } else {
                                  setSelectedLeads([]);
                                }
                              }}
                              colorScheme="blue"
                            />
                          </Th>
                          <Th>User Details</Th>
                          <Th>Property Type</Th>
                          <Th>Ad Type</Th>
                          <Th>Location</Th>
                          <Th>Comment</Th>
                          <Th>Classified Role</Th>
                          <Th>Classified Date</Th>
                          <Th>Last Activity</Th>
                          <Th textAlign="center">Status</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {requirementData.map((req) => (
                          <Tr
                            key={req._id}
                            _hover={{ bg: "blue.50" }}
                            transition="all 0.2s"
                          >
                            {/* Checkbox */}
                            <Td textAlign="center">
                              <Checkbox
                                isChecked={selectedLeads.includes(req._id)}
                                onChange={() => handleCheckboxChange(req._id)}
                                colorScheme="blue"
                              />
                            </Td>

                            {/* User Info */}
                            <Td>
                              <VStack align="flex-start" spacing={0}>
                                <Text fontWeight="semibold" fontSize="sm">
                                  {req.user_data?.full_name}
                                </Text>
                                <Text fontSize="xs" color="gray.600">
                                  {req.user_data?.phone}
                                </Text>
                              </VStack>
                            </Td>

                            {/* Property Type */}
                            <Td>
                              <Badge
                                colorScheme={getPropertyTypeBadgeColor(
                                  req.property_type
                                )}
                              >
                                {req.property_type}
                              </Badge>
                            </Td>

                            {/* Ad Type */}
                            <Td>
                              <Badge
                                colorScheme={getAdTypeBadgeColor(req.ad_type)}
                                textTransform="capitalize"
                              >
                                {req.ad_type}
                              </Badge>
                            </Td>

                            {/* Location */}
                            <Td>
                              <HStack spacing={1}>
                                <Text fontSize="sm" color="gray.700">
                                  📍 {formatLocation(req.location)}
                                </Text>
                                <Text fontSize="sm" fontWeight="bold">
                                  ({metersToKm(req.distance)} km)
                                </Text>
                              </HStack>
                            </Td>

                            {/* Comment */}
                            <Td maxW="250px">
                              {req.comment ? (
                                <Tooltip
                                  label={req.comment}
                                  placement="top"
                                  hasArrow
                                  maxW="400px"
                                >
                                  <Text
                                    fontSize="sm"
                                    noOfLines={1}
                                    cursor="pointer"
                                  >
                                    💬 {req.comment}
                                  </Text>
                                </Tooltip>
                              ) : (
                                <Text fontSize="sm" color="gray.400">
                                  —
                                </Text>
                              )}
                            </Td>

                            {/* User Role */}
                            <Td>
                              {req.user_data?.classified_primary_role ? (
                                <Badge colorScheme="teal" fontSize="xs">
                                  {req.user_data.classified_primary_role}
                                </Badge>
                              ) : (
                                <Badge colorScheme="red" fontSize="xs">
                                  Not Classified
                                </Badge>
                              )}
                            </Td>

                            {/* Classified date */}
                            <Td>
                              <Text fontSize="xs" color="gray.600">
                                {formatDate(req.created_at)}
                              </Text>
                            </Td>
                            <Td>
                              <Text fontSize="xs" color="gray.600">
                                {formatDate(req.last_activity)}
                              </Text>
                            </Td>

                            {/* Status */}
                            <Td>
                              <VStack spacing={1} align="center">
                                {directLeads[req?._id]?.viewed ? (
                                  <Badge
                                    bg={"green.500"}
                                    color={"white"}
                                    fontSize="xs"
                                    minW="70px"
                                    textAlign="center"
                                    borderRadius="full"
                                    px={2}
                                    py={1}
                                  >
                                    Viewed
                                  </Badge>
                                ) : directLeads[req?._id] ? (
                                  <Badge
                                    bg={"blue.500"}
                                    color={"white"}
                                    fontSize="xs"
                                    minW="70px"
                                    textAlign="center"
                                    borderRadius="full"
                                    px={2}
                                    py={1}
                                  >
                                    Shared
                                  </Badge>
                                ) : null}
                              </VStack>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                )}
              </VStack>

              {/* Pagination */}
              {totalPages > 1 && (
                <>
                  <Divider />
                  <Flex justify="center" align="center" gap={4}>
                    <IconButton
                      icon={<ChevronLeftIcon />}
                      onClick={() => handlePageChange(currentPage - 1)}
                      isDisabled={currentPage === 1}
                      size="sm"
                      aria-label="Previous page"
                    />
                    <Text fontSize="sm">
                      Page {currentPage} of {totalPages}
                    </Text>
                    <IconButton
                      icon={<ChevronRightIcon />}
                      onClick={() => handlePageChange(currentPage + 1)}
                      isDisabled={currentPage === totalPages}
                      size="sm"
                      aria-label="Next page"
                    />
                  </Flex>
                </>
              )}
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
      <Modal
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        isCentered
      >
        <ModalOverlay />
        <ModalContent size='xl'>
          <ModalBody p={6} textAlign="center">
            <Flex justifyContent={"space-between"} alignItems={"center"}>
              <Text fontSize="lg" fontWeight="bold" mb={4}>
                {confirmType === "all"
                  ? `Send all ${totalCount} leads?`
                  : `Send ${selectedLeads.length} leads?`}
              </Text>
              <Flex gap={'0.5rem'}>
                <Checkbox
                  isChecked={sendSMS}
                  onChange={(e) => {
                    setSendSMS(e.target.checked);
                  }}
                >
                  Send SMS
                </Checkbox>
                <Checkbox
                  isChecked={sendWhatsapp}
                  onChange={(e) => {
                    setSendWhatsapp(e.target.checked);
                    getCurrentMessage();
                  }}
                >
                  Whatsapp
                </Checkbox>
              </Flex>
            </Flex>

            <Text fontSize="sm" color="gray.600" mb={6}>
              This action will send lead details to the respective users. Are
              you sure you want to proceed?
            </Text>

            {sendWhatsapp && (
              <Flex gap={2} direction={"column"}>
                <Textarea
                  value={whatsappMessage}
                  onChange={(e) => setWhatsappMessage(e.target.value)}
                  minHeight={250}
                />
              </Flex>
            )}

            <Flex justify="center" gap={4}>
              <Button
                colorScheme="blue"
                onClick={() => {
                  if (confirmType === "all") {
                    handleSendAllLeads();
                  } else {
                    handleSendSelectedLeads();
                  }
                }}
              >
                Yes, Send
              </Button>
              <Button variant="outline" onClick={() => setIsConfirmOpen(false)}>
                Cancel
              </Button>
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default memo(NearbyLeadsModal);
